import "./SopModal.module.scss";

import {boundMethod} from "autobind-decorator";
import {debounce} from "lodash";
import React from "react";
import {OutPortal, createHtmlPortalNode} from "react-reverse-portal";

import {ESopLicenses} from "@/components/license/models";
import license from "@/services/license";
import {ILicenses} from "@/services/models";
import {ESopTab} from "@shared/sop/sop-button/models";
import {CLOSING_DELAY} from "@toolbox/models";

import PagerPageSizeFooter from "@toolbox/building-blocks/PagerPageSizeFooter";
import Modal from "@toolbox/modals/Modal";
import T, {parseNumber} from "@translate/T";
import SopTemplate from "./SopTemplate";
import {renderSopModalMenu} from "./functions";

interface ISopModalProps {
    project: number;
    licenses: ILicenses;

    hrefTarget?: string;

    afterClose(): void;
}

interface ISopModalState {
    tab: ESopTab;
}

class SopModal extends React.PureComponent<ISopModalProps, ISopModalState> {
    public readonly state: ISopModalState = {tab: ESopTab.Standard};

    private readonly portal = createHtmlPortalNode<PagerPageSizeFooter>();
    private readonly ref = React.createRef<Modal>();
    private readonly close = debounce(
        () => this.ref.current?.triggerClose(),
        CLOSING_DELAY,
    );

    private get hasTemplates() {
        return license.status.sops.includes(ESopLicenses.Template);
    }

    public componentWillUnmount() {
        this.resetTimer();
    }

    @boundMethod
    public onClick(e: React.SyntheticEvent<HTMLAnchorElement>) {
        e.preventDefault();

        const id = e.currentTarget.dataset.tab as string;
        this.setState({tab: parseNumber(id)});
    }

    @boundMethod
    public startTimer() {
        window.addEventListener("focus", this.resetTimer, {passive: true});
        this.close();
    }

    @boundMethod
    public resetTimer() {
        window.removeEventListener("focus", this.resetTimer);
        this.close.cancel();
    }

    public render() {
        const {afterClose} = this.props;
        const {tab} = this.state;
        const len = 4;

        return (
            <Modal
                ref={this.ref}
                header={<T>Create new SOP</T>}
                size={this.getModalSize(len)}
                afterClose={afterClose}
            >
                <div className="modal-body">
                    {this.renderTabs()}
                    <div styleName="sop-tab">{this.renderContent(len)}</div>
                </div>

                {tab === ESopTab.Template && <OutPortal node={this.portal} />}
            </Modal>
        );
    }

    private renderTabs() {
        if (!this.hasTemplates) {
            return null;
        }

        return (
            <ul className="nav nav-tabs mb-2" role="tablist">
                <li className="nav-item tab">
                    <a
                        id={"analyzed-tab-" + ESopTab.Standard}
                        className="nav-link active"
                        aria-controls="standard"
                        aria-selected="true"
                        data-tab={ESopTab.Standard}
                        data-toggle="tab"
                        href="#standard"
                        role="tab"
                        onClick={this.onClick}
                    >
                        <span className="nav-span">
                            <T>Standard SOP</T>
                        </span>
                    </a>
                </li>
                <li className="nav-item tab">
                    <a
                        id={"analyzed-tab-" + ESopTab.Template}
                        className="nav-link"
                        aria-controls="template"
                        aria-selected="false"
                        data-tab={ESopTab.Template}
                        data-toggle="tab"
                        href="#template"
                        role="tab"
                        onClick={this.onClick}
                    >
                        <span className="nav-span">
                            <T>SOP template</T>
                        </span>
                    </a>
                </li>
            </ul>
        );
    }

    private renderContent(columns: number) {
        const {licenses, hrefTarget, project} = this.props;

        return (
            <div className="tab-content">
                <div
                    id="standard"
                    role="tabpanel"
                    className="tab-pane fade show active"
                >
                    {this.renderMenu()}
                </div>

                <div id="template" className="tab-pane fade" role="tabpanel">
                    <SopTemplate
                        hrefTarget={hrefTarget}
                        columns={columns}
                        project={project}
                        portal={this.portal}
                        onClick={this.startTimer}
                        licenses={licenses}
                    />
                </div>
            </div>
        );
    }

    @boundMethod
    private renderMenu() {
        const {licenses, project} = this.props;

        return renderSopModalMenu(project, licenses);
    }

    private getModalSize(len: number) {
        switch (len) {
            case 1:
            case 2:
                return "md";

            case 3:
                return "lg";

            case 4:
            default:
                return "xl";
        }
    }
}

export default SopModal;
